import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { I18nField } from './AdminHero';
import { Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const AdminContact = () => {
  const { refresh } = useSiteConfig();
  const [contact, setContact] = useState({
    address: '', phone: '', email: '', hours: '',
    instagram: '', facebook: '', tiktok: '', whatsapp: '', maps_url: '',
  });
  const [footer, setFooter] = useState({ tagline: {}, rights: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get('/admin/contact'), api.get('/admin/footer')])
      .then(([c, f]) => {
        setContact((p) => ({ ...p, ...c.data }));
        setFooter((p) => ({ ...p, ...f.data }));
      })
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all([
        api.put('/admin/contact', contact),
        api.put('/admin/footer', footer),
      ]);
      toast.success('Saved!');
      refresh();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-gray-500">Loading...</div>;

  const F = ({ label, value, onChange, ...rest }) => (
    <div>
      <Label className="mb-2 block text-sm">{label}</Label>
      <Input value={value || ''} onChange={(e) => onChange(e.target.value)} className="h-11" {...rest} />
    </div>
  );

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h1 className="font-display text-2xl text-[#5a1d3b]" style={{ fontWeight: 500 }}>Contact &amp; Footer</h1>
        <p className="text-gray-500 text-sm">Contact details and social links shown on the site.</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
        <h2 className="font-display text-lg text-[#5a1d3b]" style={{ fontWeight: 500 }}>Contact Info</h2>
        <F label="Address" value={contact.address} onChange={(v) => setContact({ ...contact, address: v })} />
        <div className="grid sm:grid-cols-2 gap-4">
          <F label="Phone" value={contact.phone} onChange={(v) => setContact({ ...contact, phone: v })} />
          <F label="Email" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} type="email" />
        </div>
        <div>
          <Label className="mb-2 block text-sm">Opening Hours (multi-line)</Label>
          <Textarea
            rows={3}
            value={contact.hours || ''}
            onChange={(e) => setContact({ ...contact, hours: e.target.value })}
            placeholder={'Mo-Fr: 09:00 - 19:00\nSa: 10:00 - 17:00\nSo: Closed'}
          />
        </div>
        <F label="Google Maps URL" value={contact.maps_url} onChange={(v) => setContact({ ...contact, maps_url: v })} />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
        <h2 className="font-display text-lg text-[#5a1d3b]" style={{ fontWeight: 500 }}>Social Links</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <F label="Instagram URL" value={contact.instagram} onChange={(v) => setContact({ ...contact, instagram: v })} />
          <F label="Facebook URL" value={contact.facebook} onChange={(v) => setContact({ ...contact, facebook: v })} />
          <F label="TikTok URL" value={contact.tiktok} onChange={(v) => setContact({ ...contact, tiktok: v })} />
          <F label="WhatsApp" value={contact.whatsapp} onChange={(v) => setContact({ ...contact, whatsapp: v })} placeholder="+49..." />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
        <h2 className="font-display text-lg text-[#5a1d3b]" style={{ fontWeight: 500 }}>Footer</h2>
        <I18nField label="Tagline" value={footer.tagline} onChange={(v) => setFooter({ ...footer, tagline: v })} />
        <I18nField label="Rights line (e.g. All rights reserved.)" value={footer.rights} onChange={(v) => setFooter({ ...footer, rights: v })} />
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#c4477a] hover:bg-[#a83668] text-white rounded-full h-12 px-8 gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default AdminContact;
